import { Instagram, Youtube, Facebook } from "lucide-react";
import { useScrollReveal } from "../lib/useScrollReveal";
import { BRAND } from "../lib/brand";

// WhatsApp icon (lucide doesn't ship one)
const WhatsAppIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor">
    <path d="M19.11 17.23c-.28-.14-1.63-.8-1.88-.9-.25-.09-.44-.14-.62.14-.19.28-.71.9-.87 1.09-.16.19-.32.21-.6.07-.28-.14-1.16-.43-2.22-1.36-.82-.73-1.37-1.63-1.53-1.9-.16-.28-.02-.43.12-.57.12-.12.28-.32.42-.48.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.62-1.49-.85-2.04-.22-.54-.45-.47-.62-.48-.16-.01-.35-.01-.54-.01-.19 0-.49.07-.74.35-.25.28-.97.95-.97 2.31 0 1.36.99 2.67 1.13 2.86.14.19 1.96 2.99 4.74 4.19.66.29 1.18.46 1.58.59.66.21 1.27.18 1.74.11.53-.08 1.63-.67 1.86-1.31.23-.64.23-1.18.16-1.31-.06-.12-.25-.19-.54-.33zM16.03 5.33c-5.88 0-10.67 4.79-10.67 10.67 0 1.88.49 3.72 1.42 5.33L5.33 26.67l5.47-1.43c1.55.85 3.31 1.31 5.23 1.31 5.88 0 10.67-4.79 10.67-10.67S21.91 5.33 16.03 5.33zm0 19.52c-1.71 0-3.4-.46-4.86-1.33l-.35-.21-3.6.94.96-3.51-.23-.36a8.82 8.82 0 01-1.36-4.71c0-4.88 3.97-8.86 8.86-8.86 2.37 0 4.59.92 6.26 2.6a8.82 8.82 0 012.6 6.26c-.01 4.89-3.98 8.86-8.86 8.86z" />
  </svg>
);

const CARDS = [
  {
    key: "whatsapp",
    label: "WhatsApp",
    handle: "Chat with us",
    desc: "Quick replies, class queries, private session slots.",
    cta: "Message",
    Icon: WhatsAppIcon,
    color: "#25D366",
    href: BRAND.contact.whatsapp,
  },
  {
    key: "instagram",
    label: "Instagram",
    handle: "@yogiinit",
    desc: "Daily inspiration, breathwork reels & behind-the-scenes.",
    cta: "Follow",
    Icon: Instagram,
    color: "#E1306C",
    href: BRAND.contact.instagram,
  },
  {
    key: "youtube",
    label: "YouTube",
    handle: "@yogiinit",
    desc: "Guided practices, full tutorials & transformation talks.",
    cta: "Subscribe",
    Icon: Youtube,
    color: "#FF0000",
    href: BRAND.contact.youtube,
  },
  {
    key: "facebook",
    label: "Facebook",
    handle: "@yogiinit",
    desc: "Community stories, updates & live event announcements.",
    cta: "Connect",
    Icon: Facebook,
    color: "#1877F2",
    href: BRAND.contact.facebook,
  },
];

export const Community = () => {
  const ref = useScrollReveal();
  return (
    <section
      id="community"
      data-testid="community-section"
      ref={ref}
      className="py-24 md:py-36 bg-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Stay Connected
          </p>
          <h2
            data-testid="community-heading"
            className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light"
          >
            Join <em className="italic">Our</em> Community
          </h2>
          <p className="reveal mt-5 text-[#2C5F4A]/80 font-light">
            Connect with us and be part of the Yogiinit family.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {CARDS.map(({ key, label, handle, desc, cta, Icon, color, href }) => (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noreferrer"
              data-testid={`community-${key}`}
              className="reveal group relative rounded-[28px] bg-white border border-[#1F4A38]/10 p-7 md:p-8 shadow-[0_10px_40px_rgba(31,74,56,0.06)] hover:shadow-[0_22px_60px_rgba(31,74,56,0.14)] transition-all duration-500 hover:-translate-y-1.5 flex flex-col min-h-[260px]"
            >
              <div
                className="h-14 w-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundColor: `${color}14`,
                  color,
                }}
              >
                <Icon size={26} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif-display text-2xl text-[#153326] font-medium">
                {label}
              </h3>
              <p className="mt-1 text-sm text-[#2C5F4A]/80 tracking-wide">
                {handle}
              </p>
              <p className="mt-4 text-[13.5px] text-[#2C5F4A] leading-relaxed flex-1">
                {desc}
              </p>
              <span
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-all"
                style={{ color }}
              >
                {cta}
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </span>
              <span
                className="absolute left-7 right-7 bottom-6 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: `linear-gradient(90deg, ${color}00, ${color}99, ${color}00)`,
                }}
              />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Community;
